import { lookup } from "@repo/vihangam-yog-shared";
import { Plus, X } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";

import { CHAPTER } from "../chapter";
import { friendlyError } from "../lib/friendlyError";
import { trpc } from "../trpc";

import { formatMoney } from "./event-registrations/shared";

/** A member row's status column, as the backend writes it. */
function statusOf(status: string): Status {
  // SAFETY: `status` is written only from the Status union by the backend; the router types it as string.
  return status as Status;
}
type Status = "pending" | "active" | "cancelled";
type MembershipType = "individual" | "family";
type Gender = "" | "M" | "F";

interface FamilyMember {
  name: string;
  gender: Gender;
}

interface MemberDraft {
  fullName: string;
  gender: Gender;
  address: string;
  email: string;
  contactNo: string;
  membershipType: MembershipType;
  familyMembers: FamilyMember[];
  amountPaidDollars: string;
  paymentMethod: string;
  notes: string;
  appliedAt: string;
}

const STATUS_LABEL = {
  pending: "Waiting for payment",
  active: "Active",
  cancelled: "Cancelled",
} satisfies Record<Status, string>;

const TABS: Status[] = ["pending", "active", "cancelled"];

const EMPTY_TAB_TEXT = {
  pending: "Nobody is waiting for payment.",
  active: "No active members yet.",
  cancelled: "No cancelled memberships.",
} satisfies Record<Status, string>;

const MEMBERSHIP_LABEL = {
  individual: "Individual",
  family: "Family",
} satisfies Record<MembershipType, string>;

const GENDER_LABEL = { M: "Male", F: "Female" };

const NOT_RECORDED = "Not recorded";

/** The confirm buttons appear where their trigger was, so the second tap
 *  of a double-tap would land on them. Ignore taps this soon after. */
const CONFIRM_DEAD_MS = 400;

/** Today on this device's calendar, as the date input wants it. */
function localToday(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function emptyDraft(): MemberDraft {
  return {
    fullName: "",
    gender: "",
    address: "",
    email: "",
    contactNo: "",
    membershipType: "individual",
    familyMembers: [],
    amountPaidDollars: "",
    paymentMethod: "",
    notes: "",
    appliedAt: localToday(),
  };
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function membershipLabel(type: string): string {
  return lookup(MEMBERSHIP_LABEL, type) ?? type;
}

function genderLabel(gender: string | null | undefined): string {
  return (gender ? lookup(GENDER_LABEL, gender) : undefined) ?? NOT_RECORDED;
}

function firstNameOf(fullName: string): string {
  return fullName.trim().split(/\s+/)[0] || fullName;
}

const buttonBase =
  "inline-flex min-h-11 items-center justify-center gap-2 rounded-full px-5 text-label font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50";
const primaryButton = `${buttonBase} bg-[color:var(--color-ink)] text-[color:var(--color-paper)] hover:opacity-85`;
const secondaryButton = `${buttonBase} border border-[color:var(--color-line-strong)] text-[color:var(--color-ink)] hover:border-[color:var(--color-ink)]`;
const dangerButton = `${buttonBase} border border-[color:var(--color-danger)] text-[color:var(--color-danger)] hover:bg-[color:var(--color-danger)]/8`;

const inputClass =
  "min-h-11 w-full rounded-md border border-[color:var(--color-line-strong)] bg-[color:var(--color-paper)] px-3.5 py-2 text-body text-[color:var(--color-ink)] outline-none placeholder:text-[color:var(--color-ink-faint)] focus:border-[color:var(--color-amber)]";

/**
 * Society membership forms (Australia). New forms are typed in from
 * paper and wait under "Waiting for payment" until someone marks the fee
 * received, which makes the membership active.
 */
export function Members() {
  const [tab, setTab] = useState<Status>("pending");
  const [drawerId, setDrawerId] = useState<string | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const tabRefs = useRef<Partial<Record<Status, HTMLButtonElement | null>>>({});

  const utils = trpc.useUtils();

  const list = trpc.adminListMembers.useQuery(
    { status: tab },
    // Keep the last answer while another tab loads so the counts in the
    // tabs don't blink out; its rows are hidden until the new list lands.
    { retry: false, placeholderData: (previous) => previous },
  );
  const counts = list.data?.counts;
  const rows = list.isPlaceholderData ? null : list.data?.members;

  const refresh = () => {
    void utils.adminListMembers.invalidate();
    if (drawerId) {
      void utils.adminGetMember.invalidate({ id: drawerId });
    }
  };

  function selectTab(next: Status) {
    setTab(next);
    setDrawerId(null);
  }

  function onTabKey(e: React.KeyboardEvent, current: Status) {
    const step = e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : 0;
    if (step === 0) {
      return;
    }
    e.preventDefault();
    const index = TABS.indexOf(current);
    const next = TABS[(index + step + TABS.length) % TABS.length] ?? current;
    selectTab(next);
    tabRefs.current[next]?.focus();
  }

  return (
    <section className="mx-auto max-w-6xl px-5 py-10 md:px-10 md:py-12">
      <header className="flex flex-wrap items-end justify-between gap-x-6 gap-y-4 border-b border-[color:var(--color-line)] pb-6">
        <div className="min-w-0 flex-1">
          <h1 className="page-title">Members</h1>
          <p className="mt-3 max-w-2xl text-body text-[color:var(--color-ink-soft)]">
            Membership forms for {CHAPTER.legalName}. New forms wait under
            Waiting for payment until you mark them paid.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowNew(true)}
          className={primaryButton}
        >
          <Plus size={18} aria-hidden="true" />
          Add a membership form
        </button>
      </header>

      {notice ? (
        <p
          role="status"
          className="mt-6 rounded-lg border border-[color:var(--color-line)] bg-[color:var(--color-paper)] px-4 py-3 text-body text-[color:var(--color-ink)]"
        >
          {notice}
        </p>
      ) : null}

      <div
        role="tablist"
        aria-label="Membership status"
        className="mt-6 flex flex-wrap gap-1 border-b border-[color:var(--color-line)]"
      >
        {TABS.map((id) => {
          const isActive = id === tab;
          return (
            <button
              key={id}
              ref={(el) => {
                tabRefs.current[id] = el;
              }}
              id={`members-tab-${id}`}
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-controls="members-tab-panel"
              tabIndex={isActive ? 0 : -1}
              onClick={() => selectTab(id)}
              onKeyDown={(e) => onTabKey(e, id)}
              className={`relative min-h-11 px-4 text-[15px] tabular-nums transition-colors ${
                isActive
                  ? "font-semibold text-[color:var(--color-ink)]"
                  : "font-medium text-[color:var(--color-ink-soft)] hover:text-[color:var(--color-ink)]"
              }`}
            >
              {STATUS_LABEL[id]}
              {counts ? ` (${counts[id]})` : ""}
              {isActive ? (
                <span
                  aria-hidden="true"
                  className="absolute right-2 bottom-0 left-2 h-0.5 rounded-t bg-[color:var(--color-amber)]"
                />
              ) : null}
            </button>
          );
        })}
      </div>

      <div
        id="members-tab-panel"
        role="tabpanel"
        aria-labelledby={`members-tab-${tab}`}
      >
        {list.isLoading || (list.isFetching && !rows) ? (
          <p className="mt-8 text-body text-[color:var(--color-ink-soft)]">
            Loading members…
          </p>
        ) : null}
        {list.error ? (
          <p className="mt-8 text-body text-[color:var(--color-danger)]">
            Couldn&apos;t load members.{" "}
            {friendlyError(
              list.error,
              "Check your internet connection and try again.",
            )}
          </p>
        ) : null}

        {rows ? (
          rows.length === 0 ? (
            <p className="empty-state">{EMPTY_TAB_TEXT[tab]}</p>
          ) : (
            <>
              {/* Phones: one card per form. */}
              <ul className="mt-6 flex flex-col gap-3 md:hidden">
                {rows.map((m) => (
                  <li key={m.id}>
                    <button
                      type="button"
                      onClick={() => setDrawerId(m.id)}
                      aria-label={`Open ${m.fullName}'s membership form`}
                      className="surface flex min-h-11 w-full flex-col items-start gap-1 p-4 text-left"
                    >
                      <span className="text-body font-semibold text-[color:var(--color-ink)]">
                        {m.fullName}
                        {m.familyMembers.length > 0 ? (
                          <span className="ml-2 text-label font-normal text-[color:var(--color-ink-soft)]">
                            +{m.familyMembers.length} family
                          </span>
                        ) : null}
                      </span>
                      <span className="text-label text-[color:var(--color-ink-soft)] tabular-nums">
                        {membershipLabel(m.membershipType)} · Paid{" "}
                        {m.amountPaidCents === null
                          ? "not recorded"
                          : formatMoney(m.amountPaidCents)}
                      </span>
                      {m.contactNo ? (
                        <span className="text-label text-[color:var(--color-ink-soft)] tabular-nums">
                          {m.contactNo}
                        </span>
                      ) : null}
                      <span className="text-meta text-[color:var(--color-ink-soft)]">
                        Applied {formatDate(m.appliedAt)}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>

              {/* Wider screens: the table. */}
              <div className="mt-6 hidden overflow-x-auto md:block">
                <table className="w-full border-collapse text-left">
                  <thead>
                    <tr className="border-b border-[color:var(--color-line-strong)]">
                      {[
                        "Name",
                        "Type",
                        "Email",
                        "Phone",
                        "Paid",
                        "Date applied",
                      ].map((h) => (
                        <th
                          key={h}
                          scope="col"
                          className="table-head py-3 pr-6"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((m) => (
                      <tr
                        key={m.id}
                        className="border-b border-[color:var(--color-line)] align-top transition-colors hover:bg-[color:var(--color-paper)]"
                      >
                        <td className="py-2 pr-6">
                          <button
                            type="button"
                            onClick={() => setDrawerId(m.id)}
                            aria-label={`Open ${m.fullName}'s membership form`}
                            className="min-h-11 text-left text-body font-semibold text-[color:var(--color-ink)] underline-offset-4 hover:underline"
                          >
                            {m.fullName}
                          </button>
                          {m.familyMembers.length > 0 ? (
                            <span className="block text-meta text-[color:var(--color-ink-soft)]">
                              +{m.familyMembers.length} family
                            </span>
                          ) : null}
                        </td>
                        <td className="py-4 pr-6 text-label text-[color:var(--color-ink-soft)]">
                          {membershipLabel(m.membershipType)}
                        </td>
                        <td className="py-4 pr-6 text-label break-all">
                          <Recorded value={m.email} />
                        </td>
                        <td className="py-4 pr-6 text-label tabular-nums">
                          <Recorded value={m.contactNo} />
                        </td>
                        <td className="py-4 pr-6 text-label tabular-nums">
                          <Recorded
                            value={
                              m.amountPaidCents === null
                                ? null
                                : formatMoney(m.amountPaidCents)
                            }
                          />
                        </td>
                        <td className="py-4 pr-6 text-label whitespace-nowrap text-[color:var(--color-ink-soft)] tabular-nums">
                          {formatDate(m.appliedAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )
        ) : null}
      </div>

      {showNew ? (
        <NewMemberDrawer
          onClose={() => setShowNew(false)}
          onCreated={(fullName) => {
            setShowNew(false);
            selectTab("pending");
            setNotice(`Saved ${firstNameOf(fullName)}'s form.`);
            window.setTimeout(() => setNotice(null), 6000);
            refresh();
          }}
        />
      ) : null}

      {drawerId ? (
        <DetailDrawer
          id={drawerId}
          onClose={() => setDrawerId(null)}
          onChanged={refresh}
        />
      ) : null}
    </section>
  );
}

/** A table cell value, or a faint "Not recorded". */
function Recorded({ value }: { value: string | null }) {
  return value ? (
    <span className="text-[color:var(--color-ink-soft)]">{value}</span>
  ) : (
    <span className="text-[color:var(--color-ink-faint)]">{NOT_RECORDED}</span>
  );
}

/* ─────────────── Add a membership form ─────────────── */

function NewMemberDrawer({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (fullName: string) => void;
}) {
  const [initial] = useState<MemberDraft>(emptyDraft);
  const [draft, setDraft] = useState<MemberDraft>(initial);
  const create = trpc.adminCreateMember.useMutation({
    onSuccess: (_data, vars) => onCreated(vars.fullName),
  });
  const ids = useId();
  const dirty = JSON.stringify(draft) !== JSON.stringify(initial);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const amountCents = draft.amountPaidDollars
      ? Math.round(parseFloat(draft.amountPaidDollars) * 100)
      : undefined;
    create.mutate({
      fullName: draft.fullName.trim(),
      gender: draft.gender || undefined,
      address: draft.address.trim() || undefined,
      email: draft.email.trim() || undefined,
      contactNo: draft.contactNo.trim() || undefined,
      membershipType: draft.membershipType,
      familyMembers:
        draft.membershipType === "family" && draft.familyMembers.length > 0
          ? draft.familyMembers
              .filter((f) => f.name.trim())
              .map((f) => ({
                name: f.name.trim(),
                gender: f.gender || undefined,
              }))
          : undefined,
      amountPaidCents: amountCents,
      paymentMethod: draft.paymentMethod.trim() || undefined,
      notes: draft.notes.trim() || undefined,
      appliedAt: draft.appliedAt,
    });
  };

  return (
    <Dialog
      title="Add a membership form"
      onClose={onClose}
      // One stray tap outside a half-typed paper form used to throw all
      // the typing away. The close button and Escape still work.
      closeOnBackdrop={!dirty}
    >
      <form onSubmit={submit} className="flex flex-col gap-6">
        <Field id={`${ids}-name`} label="Full name (required)">
          <input
            id={`${ids}-name`}
            type="text"
            required
            autoComplete="off"
            value={draft.fullName}
            onChange={(e) => setDraft({ ...draft, fullName: e.target.value })}
            className={inputClass}
          />
        </Field>

        <div className="grid gap-6 md:grid-cols-2">
          <GenderChoice
            name={`${ids}-gender`}
            legend="Gender"
            value={draft.gender}
            onChange={(v) => setDraft({ ...draft, gender: v })}
          />
          <Field id={`${ids}-date`} label="Date on the form">
            <input
              id={`${ids}-date`}
              type="date"
              required
              value={draft.appliedAt}
              onChange={(e) =>
                setDraft({ ...draft, appliedAt: e.target.value })
              }
              className={inputClass}
            />
          </Field>
        </div>

        <Field id={`${ids}-address`} label="Address">
          <textarea
            id={`${ids}-address`}
            rows={2}
            value={draft.address}
            onChange={(e) => setDraft({ ...draft, address: e.target.value })}
            className={inputClass}
          />
        </Field>

        <div className="grid gap-6 md:grid-cols-2">
          <Field id={`${ids}-email`} label="Email">
            <input
              id={`${ids}-email`}
              type="email"
              autoComplete="off"
              value={draft.email}
              onChange={(e) => setDraft({ ...draft, email: e.target.value })}
              className={inputClass}
            />
          </Field>
          <Field id={`${ids}-phone`} label="Phone number">
            <input
              id={`${ids}-phone`}
              type="tel"
              autoComplete="off"
              value={draft.contactNo}
              onChange={(e) =>
                setDraft({ ...draft, contactNo: e.target.value })
              }
              className={inputClass}
            />
          </Field>
        </div>

        <fieldset className="flex flex-col gap-1">
          <legend className="mb-1 text-label font-semibold text-[color:var(--color-ink)]">
            Membership type (required)
          </legend>
          <label className="flex min-h-11 cursor-pointer items-center gap-3 text-body text-[color:var(--color-ink)]">
            <input
              type="radio"
              name={`${ids}-type`}
              checked={draft.membershipType === "individual"}
              onChange={() =>
                setDraft({
                  ...draft,
                  membershipType: "individual",
                  familyMembers: [],
                })
              }
              className="h-5 w-5 accent-[color:var(--color-amber)]"
            />
            Individual, $10
          </label>
          <label className="flex min-h-11 cursor-pointer items-center gap-3 text-body text-[color:var(--color-ink)]">
            <input
              type="radio"
              name={`${ids}-type`}
              checked={draft.membershipType === "family"}
              onChange={() => setDraft({ ...draft, membershipType: "family" })}
              className="h-5 w-5 accent-[color:var(--color-amber)]"
            />
            Family, up to 4 people, $30
          </label>
        </fieldset>

        {draft.membershipType === "family" ? (
          <fieldset className="flex flex-col gap-4">
            <legend className="mb-1 text-label font-semibold text-[color:var(--color-ink)]">
              Other family members (up to 3)
            </legend>
            {[0, 1, 2].map((i) => {
              const fm = draft.familyMembers[i] ?? { name: "", gender: "" };
              const update = (next: FamilyMember) => {
                const arr = [...draft.familyMembers];
                while (arr.length <= i) {
                  arr.push({ name: "", gender: "" });
                }
                arr[i] = next;
                setDraft({ ...draft, familyMembers: arr });
              };
              return (
                <div
                  key={i}
                  className="flex flex-col gap-2 rounded-lg border border-[color:var(--color-line)] p-3"
                >
                  <Field
                    id={`${ids}-family-${i}`}
                    label={`Family member ${i + 1}, name`}
                  >
                    <input
                      id={`${ids}-family-${i}`}
                      type="text"
                      autoComplete="off"
                      value={fm.name}
                      onChange={(e) => update({ ...fm, name: e.target.value })}
                      className={inputClass}
                    />
                  </Field>
                  <GenderChoice
                    name={`${ids}-family-${i}-gender`}
                    legend={`Family member ${i + 1}, gender`}
                    value={fm.gender}
                    onChange={(v) => update({ ...fm, gender: v })}
                  />
                </div>
              );
            })}
          </fieldset>
        ) : null}

        <div className="grid gap-6 md:grid-cols-2">
          <Field
            id={`${ids}-amount`}
            label="Amount paid, in dollars"
            hint="Leave empty if they haven't paid yet."
          >
            <input
              id={`${ids}-amount`}
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              aria-describedby={`${ids}-amount-hint`}
              value={draft.amountPaidDollars}
              onChange={(e) =>
                setDraft({ ...draft, amountPaidDollars: e.target.value })
              }
              className={inputClass}
            />
          </Field>
          <Field id={`${ids}-method`} label="How they paid">
            <input
              id={`${ids}-method`}
              type="text"
              autoComplete="off"
              placeholder="For example: bank transfer or cash"
              value={draft.paymentMethod}
              onChange={(e) =>
                setDraft({ ...draft, paymentMethod: e.target.value })
              }
              className={inputClass}
            />
          </Field>
        </div>

        <Field id={`${ids}-notes`} label="Notes (only the team sees these)">
          <textarea
            id={`${ids}-notes`}
            rows={3}
            value={draft.notes}
            onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
            className={inputClass}
          />
        </Field>

        {create.error ? (
          <p
            role="alert"
            className="text-label text-[color:var(--color-danger)]"
          >
            {friendlyError(create.error)}
          </p>
        ) : null}

        <div className="sticky bottom-0 -mx-5 flex flex-col gap-3 border-t border-[color:var(--color-line)] bg-[color:var(--color-bg-soft)] px-5 py-4 md:-mx-7 md:px-7">
          <p className="text-label text-[color:var(--color-ink-soft)]">
            It goes under Waiting for payment. Mark it active once the fee is
            in.
          </p>
          <div className="flex flex-wrap items-center justify-end gap-3">
            <button type="button" onClick={onClose} className={secondaryButton}>
              Cancel
            </button>
            <button
              type="submit"
              disabled={!draft.fullName.trim() || create.isPending}
              className={primaryButton}
            >
              {create.isPending ? "Saving…" : "Save form"}
            </button>
          </div>
        </div>
      </form>
    </Dialog>
  );
}

/* ─────────────── One membership form ─────────────── */

function DetailDrawer({
  id,
  onClose,
  onChanged,
}: {
  id: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const detail = trpc.adminGetMember.useQuery({ id }, { retry: false });
  // When the "Cancel membership" or "Delete" confirm was shown; null when
  // it isn't. Guards the double-tap (see CONFIRM_DEAD_MS).
  const [cancelArmedAt, setCancelArmedAt] = useState<number | null>(null);
  const [deleteArmedAt, setDeleteArmedAt] = useState<number | null>(null);
  const update = trpc.adminUpdateMember.useMutation({
    onSuccess: () => {
      setCancelArmedAt(null);
      onChanged();
    },
  });
  const del = trpc.adminDeleteMember.useMutation({
    onSuccess: () => {
      onChanged();
      onClose();
    },
  });

  const member = detail.data;
  const busy = update.isPending || del.isPending;
  const savingStatus = update.isPending ? update.variables.patch.status : null;

  const setStatus = (status: Status) =>
    member && update.mutate({ id, patch: { status } });

  function confirmed(armedAt: number | null): boolean {
    return armedAt !== null && Date.now() - armedAt >= CONFIRM_DEAD_MS;
  }

  const status = member ? statusOf(member.status) : null;
  const firstName = member ? firstNameOf(member.fullName) : "";

  return (
    <Dialog
      title={member?.fullName ?? "Membership form"}
      onClose={onClose}
      closeOnBackdrop
    >
      {detail.isLoading ? (
        <p className="text-body text-[color:var(--color-ink-soft)]">
          Loading this form…
        </p>
      ) : null}
      {detail.error ? (
        <p className="text-body text-[color:var(--color-danger)]">
          Couldn&apos;t load this form.{" "}
          {friendlyError(
            detail.error,
            "Check your internet connection and try again.",
          )}
        </p>
      ) : null}
      {member && status ? (
        <div className="flex flex-col gap-8">
          {/* Status and what can be done about it */}
          <div className="flex flex-col gap-3">
            <p className="flex flex-wrap items-center gap-2 text-body text-[color:var(--color-ink)]">
              Status: <StatusChip status={status} />
            </p>
            <div className="flex flex-wrap items-center gap-2">
              {status !== "active" ? (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => setStatus("active")}
                  className={`${buttonBase} bg-[color:var(--color-amber)] text-[color:var(--color-paper)] hover:opacity-90`}
                >
                  {savingStatus === "active"
                    ? "Saving…"
                    : "Mark as paid and active"}
                </button>
              ) : null}
              {status !== "pending" ? (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => setStatus("pending")}
                  className={secondaryButton}
                >
                  {savingStatus === "pending"
                    ? "Saving…"
                    : "Move back to waiting"}
                </button>
              ) : null}
              {status !== "cancelled" && cancelArmedAt === null ? (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => setCancelArmedAt(Date.now())}
                  className={secondaryButton}
                >
                  Cancel membership
                </button>
              ) : null}
            </div>
            {status !== "cancelled" && cancelArmedAt !== null ? (
              <InlineConfirm
                question={`Cancel ${firstName}'s membership?`}
                yesLabel={
                  savingStatus === "cancelled"
                    ? "Saving…"
                    : "Yes, cancel membership"
                }
                busy={busy}
                onYes={() => {
                  if (confirmed(cancelArmedAt)) {
                    setStatus("cancelled");
                  }
                }}
                onNo={() => setCancelArmedAt(null)}
              />
            ) : null}
            {update.error ? (
              <p
                role="alert"
                className="text-label text-[color:var(--color-danger)]"
              >
                {friendlyError(update.error)}
              </p>
            ) : null}
          </div>

          <DetailGrid
            rows={[
              ["Full name", member.fullName],
              ["Gender", genderLabel(member.gender)],
              ["Date on the form", formatDate(member.appliedAt)],
              ["Email", member.email ?? NOT_RECORDED],
              ["Phone number", member.contactNo ?? NOT_RECORDED],
              ["Address", member.address ?? NOT_RECORDED],
            ]}
          />

          <div>
            <h3 className="text-title font-bold text-[color:var(--color-ink)]">
              Membership
            </h3>
            <p className="mt-2 text-body text-[color:var(--color-ink)]">
              {membershipLabel(member.membershipType)}
            </p>
            {member.familyMembers.length > 0 ? (
              <ol className="mt-3 flex flex-col gap-1.5 text-body text-[color:var(--color-ink)]">
                {member.familyMembers.map((fm, i) => (
                  <li key={i} className="flex flex-wrap items-baseline gap-x-3">
                    <span className="w-5 text-label text-[color:var(--color-ink-soft)] tabular-nums">
                      {i + 1}.
                    </span>
                    <span>{fm.name}</span>
                    {fm.gender ? (
                      <span className="text-label text-[color:var(--color-ink-soft)]">
                        {genderLabel(fm.gender)}
                      </span>
                    ) : null}
                  </li>
                ))}
              </ol>
            ) : null}
          </div>

          <DetailGrid
            rows={[
              [
                "Amount paid",
                member.amountPaidCents === null
                  ? NOT_RECORDED
                  : formatMoney(member.amountPaidCents),
              ],
              ["How they paid", member.paymentMethod ?? NOT_RECORDED],
              [
                "Payment received",
                member.paymentReceivedAt
                  ? formatDate(member.paymentReceivedAt)
                  : NOT_RECORDED,
              ],
            ]}
          />

          {member.notes ? (
            <div>
              <h3 className="text-title font-bold text-[color:var(--color-ink)]">
                Notes
              </h3>
              <p className="mt-2 text-body whitespace-pre-wrap text-[color:var(--color-ink)]">
                {member.notes}
              </p>
            </div>
          ) : null}

          <p className="border-t border-[color:var(--color-line)] pt-4 text-meta text-[color:var(--color-ink-soft)]">
            Added {formatDate(member.createdAt)} · Last changed{" "}
            {formatDate(member.updatedAt)}
          </p>

          <div className="flex flex-col gap-2">
            {deleteArmedAt === null ? (
              <button
                type="button"
                disabled={busy}
                onClick={() => setDeleteArmedAt(Date.now())}
                className={`${dangerButton} self-start`}
              >
                Delete this record for good
              </button>
            ) : (
              <InlineConfirm
                question={`Delete ${member.fullName}'s record? This can't be undone.`}
                yesLabel={del.isPending ? "Deleting…" : "Yes, delete"}
                busy={busy}
                danger
                onYes={() => {
                  if (confirmed(deleteArmedAt)) {
                    del.mutate({ id });
                  }
                }}
                onNo={() => setDeleteArmedAt(null)}
              />
            )}
            {del.error ? (
              <p
                role="alert"
                className="text-label text-[color:var(--color-danger)]"
              >
                {friendlyError(del.error)}
              </p>
            ) : null}
          </div>
        </div>
      ) : null}
    </Dialog>
  );
}

/* ─────────────── small bits ─────────────── */

function InlineConfirm({
  question,
  yesLabel,
  busy,
  danger = false,
  onYes,
  onNo,
}: {
  question: string;
  yesLabel: string;
  busy: boolean;
  danger?: boolean;
  onYes: () => void;
  onNo: () => void;
}) {
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-[color:var(--color-line-strong)] bg-[color:var(--color-paper)] p-4">
      <p className="text-body font-semibold text-[color:var(--color-ink)]">
        {question}
      </p>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={onYes}
          className={`${buttonBase} ${
            danger
              ? "bg-[color:var(--color-danger)] text-[color:var(--color-paper)]"
              : "bg-[color:var(--color-ink)] text-[color:var(--color-paper)]"
          } hover:opacity-85`}
        >
          {yesLabel}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={onNo}
          className={secondaryButton}
        >
          Keep it
        </button>
      </div>
    </div>
  );
}

function Field({
  id,
  label,
  hint,
  children,
}: {
  id: string;
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label
        htmlFor={id}
        className="text-label font-semibold text-[color:var(--color-ink)]"
      >
        {label}
      </label>
      {children}
      {hint ? (
        <p
          id={`${id}-hint`}
          className="text-label text-[color:var(--color-ink-soft)]"
        >
          {hint}
        </p>
      ) : null}
    </div>
  );
}

const GENDER_OPTIONS: [Gender, string][] = [
  ["M", "Male"],
  ["F", "Female"],
  ["", "Not given"],
];

function GenderChoice({
  name,
  legend,
  value,
  onChange,
}: {
  name: string;
  legend: string;
  value: Gender;
  onChange: (v: Gender) => void;
}) {
  return (
    <fieldset>
      <legend className="mb-1 text-label font-semibold text-[color:var(--color-ink)]">
        {legend}
      </legend>
      <div className="flex flex-wrap gap-x-5">
        {GENDER_OPTIONS.map(([option, label]) => (
          <label
            key={label}
            className="flex min-h-11 cursor-pointer items-center gap-2.5 text-body text-[color:var(--color-ink)]"
          >
            <input
              type="radio"
              name={name}
              checked={value === option}
              onChange={() => onChange(option)}
              className="h-5 w-5 accent-[color:var(--color-amber)]"
            />
            {label}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function StatusChip({ status }: { status: Status }) {
  const tone =
    status === "active"
      ? { background: "var(--color-amber)", color: "var(--color-paper)" }
      : status === "pending"
        ? { background: "var(--color-peach-deep)", color: "var(--color-ink)" }
        : {
            background: "color-mix(in srgb, var(--color-ink) 12%, transparent)",
            color: "var(--color-ink)",
          };
  return (
    <span className="badge" style={tone}>
      {STATUS_LABEL[status]}
    </span>
  );
}

function DetailGrid({ rows }: { rows: [string, string][] }) {
  return (
    <dl className="grid gap-x-6 gap-y-4 md:grid-cols-2">
      {rows.map(([k, v]) => (
        <div key={k} className="flex flex-col gap-0.5">
          <dt className="text-label font-medium text-[color:var(--color-ink-soft)]">
            {k}
          </dt>
          <dd
            className={`text-body break-words ${
              v === NOT_RECORDED
                ? "text-[color:var(--color-ink-faint)]"
                : "text-[color:var(--color-ink)]"
            }`}
          >
            {v}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * The side panel both forms open in. It is a modal dialog: announced as
 * one, focus moves into it, Escape and the close button always close it,
 * and a tap on the dimmed page closes it only when nothing would be lost.
 */
function Dialog({
  title,
  onClose,
  closeOnBackdrop,
  children,
}: {
  title: string;
  onClose: () => void;
  closeOnBackdrop: boolean;
  children: React.ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  useEffect(() => {
    const opener =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    panelRef.current?.focus();
    return () => opener?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch justify-end"
      style={{
        background: "color-mix(in srgb, var(--color-shadow) 45%, transparent)",
      }}
      onClick={closeOnBackdrop ? onClose : undefined}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="ml-auto flex h-full w-full max-w-2xl flex-col overflow-y-auto bg-[color:var(--color-bg)] px-5 py-6 shadow-2xl outline-none md:px-7"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-6 flex items-start justify-between gap-4">
          <h2
            id={titleId}
            className="text-heading font-bold break-words text-[color:var(--color-ink)]"
          >
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-[color:var(--color-ink-soft)] transition-colors hover:bg-[color:var(--color-paper)] hover:text-[color:var(--color-ink)]"
            aria-label="Close"
          >
            <X size={22} aria-hidden="true" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
